import {
  TableCellType,
  TableHeader,
} from "../components/custom-table/table";
import { SuperAdminTableColumnKeys } from "../enums/tableColumnKeys";

export interface SuperAdminMember {
  id: string;
  name: string;
  email: string;
  role: "Super Admin" | "Admin" | "Member";
  internalOrgIds: string[];
  externalOrgIds: string[];
  internalOrgs: string[];
  externalOrgs: string[];
  status: string;
}

export const SuperAdminTableHeaderColumns: TableHeader[] = [
  {
    label: "Member",
    key: SuperAdminTableColumnKeys.Member,
    sortable: true,
    order: 1,
    width: 14,
    type: TableCellType.STRING,
  },
  {
    label: "Mail",
    key: SuperAdminTableColumnKeys.Mail,
    sortable: true,
    order: 2,
    width: 18,
    type: TableCellType.STRING,
  },
  {
    label: "Internal Orgs",
    key: SuperAdminTableColumnKeys.InternalOrgs,
    sortable: true,
    order: 3,
    width: 22,
    type: TableCellType.BADGE_GROUP,
  },
  {
    label: "External Orgs",
    key: SuperAdminTableColumnKeys.ExternalOrgs,
    sortable: true,
    order: 4,
    width: 22,
    type: TableCellType.BADGE_GROUP,
  },
  {
    label: "Active",
    key: SuperAdminTableColumnKeys.Active,
    sortable: false,
    order: 5,
    width: 9,
    type: TableCellType.TOGGLE_SWITCH,
  },
  {
    label: "2FA",
    key: SuperAdminTableColumnKeys.Reset2FA,
    sortable: false,
    order: 6,
    width: 10,
    type: TableCellType.RESET_2FA,
  },
  {
    label: "",
    key: SuperAdminTableColumnKeys.Actions,
    sortable: false,
    order: 7,
    width: 5,
    type: TableCellType.EDIT_DELETE_ICONS,
  },
];

const getOrgsBadgeGroupConfig = (orgs: string[]) => {
  return {
    badges: orgs,
    emptyText: "None",
    allText: "All",
  };
};

interface GenerateUIDataOptions {
  onEditMember?: (member: SuperAdminMember) => void;
  onDeleteMember?: (member: SuperAdminMember) => void;
  onToggleActive?: (memberId: string, isActive: boolean) => void;
  onResetTwoFactor?: (member: SuperAdminMember) => void;
}

export function generateUIDataForSuperAdminTable(
  members: SuperAdminMember[],
  options?: GenerateUIDataOptions,
) {
  return members.map((member) => {
    const actionsData = {
      onEdit: () => {
        options?.onEditMember?.(member);
      },
      onDelete: () => {
        options?.onDeleteMember?.(member);
      },
    };

    const isActive = member.status.toLowerCase() === "active";

    const activeToggleData = {
      id: `active-toggle-${member.id}`,
      defaultChecked: isActive,
      memberId: member.id,
      onToggle: (checked: boolean) => {
        options?.onToggleActive?.(member.id, checked);
      },
    };

    const resetTwoFactorData = {
      memberId: member.id,
      onReset: () => {
        options?.onResetTwoFactor?.(member);
      },
    };

    return {
      id: member.id,
      [SuperAdminTableColumnKeys.Member]: member.name,
      [SuperAdminTableColumnKeys.Mail]: member.email,
      [SuperAdminTableColumnKeys.InternalOrgs]: getOrgsBadgeGroupConfig(
        member.internalOrgs,
      ),
      [SuperAdminTableColumnKeys.ExternalOrgs]: getOrgsBadgeGroupConfig(
        member.externalOrgs,
      ),
      [SuperAdminTableColumnKeys.Active]: activeToggleData,
      [SuperAdminTableColumnKeys.Reset2FA]: resetTwoFactorData,
      [SuperAdminTableColumnKeys.Actions]: actionsData,
    };
  });
}
